import React, { useEffect, useState } from "react";
import { Col, Container, FormGroup, Input, Label, Row } from "reactstrap";
import moment from "moment";
import CommonModal from "../../../UiKits/Modals/common/modal";

const BUSINESS_TYPES = [
  "Bar", "Restaurant / café", "Bottle shop / retailer", "Club",
  "Hotel / accommodation", "Event / catering company", "Corporate office",
];

const LICENCE_TYPES = [
  "General (pub / hotel)", "On-premises", "Packaged liquor",
  "Club", "Producer / wholesale", "Limited / event",
];

const MONTHLY_VOLUMES = [
  "Prefer not to say", "Under $1,000", "$1,000 – $5,000",
  "$5,000 – $20,000", "Over $20,000",
];

const STATUS_COLORS = {
  pending:   { bg: "#fff8e1", text: "#f59e0b", label: "Pending" },
  approved:  { bg: "#e8f5e9", text: "#16a34a", label: "Approved" },
  rejected:  { bg: "#fce4ec", text: "#dc2626", label: "Rejected" },
  suspended: { bg: "#f3e5f5", text: "#7c3aed", label: "Suspended" },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return (
    <span style={{ background: s.bg, color: s.text, padding: "3px 10px", borderRadius: "999px", fontWeight: 600, fontSize: "0.75rem" }}>
      {s.label}
    </span>
  );
};

const DetailRow = ({ label, value, valueStyle = {} }) => (
  <Col xxl={6} md={6} className="mb-2">
    <p className="mb-0 text-xs text-[#1D243399]">{label}</p>
    <p className="mb-0 text-sm font-medium text-[#1D2433]" style={valueStyle}>
      {value || "—"}
    </p>
  </Col>
);

const FieldRow = ({ label, children }) => (
  <Col xxl={6} md={6} className="mb-3">
    <FormGroup className="mb-0">
      <Label className="text-xs text-[#1D243399] mb-1">{label}</Label>
      {children}
    </FormGroup>
  </Col>
);

const SectionHeader = ({ children }) => (
  <Col xxl={12} className="mt-3 mb-1">
    <p className="text-xs font-semibold text-[#1D2433] uppercase tracking-wide mb-1">{children}</p>
    <hr className="mt-0" />
  </Col>
);

const emptyForm = () => ({
  legal_name: "", abn: "", business_type: "", licence_number: "",
  licence_type: "", licence_expiry: "", contact_name: "", contact_role: "",
  contact_email: "", contact_phone: "", monthly_volume: "Prefer not to say",
  notes: "", delivery_addresses: [],
});

/**
 * Single modal for viewing AND editing a business application.
 *
 * Props:
 *  - isOpen, toggle
 *  - data: the application row (or null)
 *  - onApprove, onReject, onSuspend: (data) => void  — status actions
 *  - onSave: (id, formData) => Promise<void>          — persist edits
 */
const BusinessApplicationModal = ({
  isOpen, toggle, data, onApprove, onReject, onSuspend, onSave,
}) => {
  const [mode, setMode] = useState("view"); // "view" | "edit"
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data) {
      setForm({
        legal_name: data.legal_name || "",
        abn: data.abn || "",
        business_type: data.business_type || "",
        licence_number: data.licence_number || "",
        licence_type: data.licence_type || "",
        licence_expiry: data.licence_expiry
          ? moment(data.licence_expiry).format("YYYY-MM-DD")
          : "",
        contact_name: data.contact_name || "",
        contact_role: data.contact_role || "",
        contact_email: data.contact_email || "",
        contact_phone: data.contact_phone || "",
        monthly_volume: data.monthly_volume || "Prefer not to say",
        notes: data.notes || "",
        delivery_addresses: data.delivery_addresses || [],
      });
    }
    setMode("view");
  }, [data]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const setAddress = (i, value) =>
    setForm((f) => {
      const next = [...f.delivery_addresses];
      next[i] = { ...next[i], address: value };
      return { ...f, delivery_addresses: next };
    });

  const setPrimary = (i) =>
    setForm((f) => ({
      ...f,
      delivery_addresses: f.delivery_addresses.map((a, idx) => ({
        ...a,
        is_primary: idx === i,
      })),
    }));

  const addAddress = () =>
    setForm((f) => ({
      ...f,
      delivery_addresses: [...f.delivery_addresses, { address: "", is_primary: f.delivery_addresses.length === 0 }],
    }));

  const removeAddress = (i) =>
    setForm((f) => ({
      ...f,
      delivery_addresses: f.delivery_addresses.filter((_, idx) => idx !== i),
    }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(data._id, form);
      setMode("view");
    } finally {
      setSaving(false);
    }
  };

  if (!data) return null;

  return (
    <CommonModal isOpen={isOpen} title="Application Details" className="store_modal" toggler={toggle} size="lg">
      <Container>
        <Row>
          {/* Header */}
          <Col xxl={12} className="mb-3">
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-3">
                <div>
                  <h6 className="mb-0 fw-bold">{data.legal_name}</h6>
                  <span className="text-xs text-[#1D243399]">{data.business_type}</span>
                </div>
                <StatusBadge status={data.status} />
              </div>
              {mode === "view" ? (
                <button type="button" onClick={() => setMode("edit")}
                  style={{ background: "#1D2433", color: "#fff", border: "none", borderRadius: "10px", padding: "6px 16px", fontWeight: 600, fontSize: "0.8rem", cursor: "pointer" }}>
                  Edit Details
                </button>
              ) : (
                <span className="text-xs" style={{ color: "#D3178A", fontWeight: 600 }}>Editing…</span>
              )}
            </div>
          </Col>

          {mode === "view" ? (
            <>
              <DetailRow label="ABN" value={data.abn} />
              <DetailRow label="Business Type" value={data.business_type} />
              <DetailRow label="Monthly Volume" value={data.monthly_volume} />
              {data.rep_tag && <DetailRow label="Rep Tag" value={data.rep_tag} />}
              {data.rejection_reason && (
                <DetailRow label="Rejection Reason" value={data.rejection_reason} valueStyle={{ color: "#dc2626" }} />
              )}

              <SectionHeader>Liquor Licence</SectionHeader>
              <DetailRow label="Licence Number" value={data.licence_number} />
              <DetailRow label="Licence Type" value={data.licence_type} />
              <DetailRow
                label="Expiry"
                value={data.licence_expiry ? moment(data.licence_expiry).format("DD/MM/YYYY") : "—"}
                valueStyle={{ color: data.licence_expiry && new Date(data.licence_expiry) < new Date() ? "#dc2626" : "#16a34a" }}
              />

              <SectionHeader>Primary Contact</SectionHeader>
              <DetailRow label="Name" value={data.contact_name} />
              <DetailRow label="Role" value={data.contact_role || "—"} />
              <DetailRow label="Email" value={data.contact_email} />
              <DetailRow label="Phone" value={data.contact_phone} />
              <DetailRow label="Registered Phone" value={data.user_id?.phoneNumber || "—"} />

              <SectionHeader>Delivery Addresses</SectionHeader>
              <Col xxl={12}>
                {data.delivery_addresses?.map((addr, i) => (
                  <div key={i} className="d-flex align-items-start gap-2 mb-1">
                    {addr.is_primary && (
                      <span style={{ background: "#fff0f7", color: "#D3178A", fontSize: "0.65rem", fontWeight: 700, padding: "1px 6px", borderRadius: "999px", whiteSpace: "nowrap", marginTop: "2px" }}>
                        Primary
                      </span>
                    )}
                    <span className="text-sm">{addr.address}</span>
                  </div>
                ))}
              </Col>

              {data.notes && (
                <>
                  <SectionHeader>Notes</SectionHeader>
                  <Col xxl={12}><p className="text-sm text-[#1D243399]">{data.notes}</p></Col>
                </>
              )}

              <SectionHeader>Timeline</SectionHeader>
              <DetailRow label="Applied" value={moment(data.createdAt).format("DD/MM/YYYY HH:mm")} />
              {data.reviewed_at && (
                <DetailRow label="Reviewed" value={moment(data.reviewed_at).format("DD/MM/YYYY HH:mm")} />
              )}

              {/* Status actions */}
              <Col xxl={12} className="mt-4 d-flex gap-2 justify-content-end">
                {(data.status === "pending" || data.status === "rejected") && (
                  <button type="button" onClick={() => { toggle(); onApprove(data); }}
                    style={{ background: "#16a34a", color: "#fff", border: "none", borderRadius: "12px", padding: "8px 20px", fontWeight: 600, cursor: "pointer" }}>
                    Approve
                  </button>
                )}
                {data.status === "pending" && (
                  <button type="button" onClick={() => { toggle(); onReject(data); }}
                    style={{ background: "#dc2626", color: "#fff", border: "none", borderRadius: "12px", padding: "8px 20px", fontWeight: 600, cursor: "pointer" }}>
                    Reject
                  </button>
                )}
                {data.status === "approved" && (
                  <button type="button" onClick={() => { toggle(); onSuspend(data); }}
                    style={{ background: "#7c3aed", color: "#fff", border: "none", borderRadius: "12px", padding: "8px 20px", fontWeight: 600, cursor: "pointer" }}>
                    Suspend
                  </button>
                )}
                <button type="button" onClick={toggle}
                  style={{ background: "#f3f4f6", color: "#374151", border: "none", borderRadius: "12px", padding: "8px 20px", fontWeight: 600, cursor: "pointer" }}>
                  Close
                </button>
              </Col>
            </>
          ) : (
            <>
              <FieldRow label="Legal Name"><Input value={form.legal_name} onChange={set("legal_name")} /></FieldRow>
              <FieldRow label="ABN"><Input value={form.abn} onChange={set("abn")} /></FieldRow>

              <FieldRow label="Business Type">
                <Input type="select" value={form.business_type} onChange={set("business_type")}>
                  {BUSINESS_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </Input>
              </FieldRow>
              <FieldRow label="Monthly Volume">
                <Input type="select" value={form.monthly_volume} onChange={set("monthly_volume")}>
                  {MONTHLY_VOLUMES.map((v) => <option key={v} value={v}>{v}</option>)}
                </Input>
              </FieldRow>

              <SectionHeader>Liquor Licence</SectionHeader>
              <FieldRow label="Licence Number"><Input value={form.licence_number} onChange={set("licence_number")} /></FieldRow>
              <FieldRow label="Licence Type">
                <Input type="select" value={form.licence_type} onChange={set("licence_type")}>
                  {LICENCE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </Input>
              </FieldRow>
              <FieldRow label="Licence Expiry">
                <Input type="date" value={form.licence_expiry} onChange={set("licence_expiry")} />
              </FieldRow>

              <SectionHeader>Primary Contact</SectionHeader>
              <FieldRow label="Name"><Input value={form.contact_name} onChange={set("contact_name")} /></FieldRow>
              <FieldRow label="Role"><Input value={form.contact_role} onChange={set("contact_role")} /></FieldRow>
              <FieldRow label="Email"><Input type="email" value={form.contact_email} onChange={set("contact_email")} /></FieldRow>
              <FieldRow label="Phone"><Input value={form.contact_phone} onChange={set("contact_phone")} /></FieldRow>

              <SectionHeader>Delivery Addresses</SectionHeader>
              <Col xxl={12}>
                {form.delivery_addresses.map((addr, i) => (
                  <div key={i} className="d-flex align-items-center gap-2 mb-2">
                    <Input
                      value={addr.address}
                      onChange={(e) => setAddress(i, e.target.value)}
                      placeholder="Street, suburb, state, postcode"
                    />
                    <label className="d-flex align-items-center gap-1 text-xs" style={{ whiteSpace: "nowrap" }}>
                      <input type="radio" name="primary_addr" checked={!!addr.is_primary} onChange={() => setPrimary(i)} />
                      Primary
                    </label>
                    <button type="button" onClick={() => removeAddress(i)}
                      style={{ background: "none", border: "none", color: "#dc2626", cursor: "pointer", fontSize: "0.8rem" }}>
                      ✕
                    </button>
                  </div>
                ))}
                <button type="button" onClick={addAddress}
                  style={{ background: "#f3f4f6", color: "#374151", border: "none", borderRadius: "8px", padding: "6px 14px", fontSize: "0.75rem", fontWeight: 600, cursor: "pointer" }}>
                  + Add address
                </button>
              </Col>

              <SectionHeader>Notes</SectionHeader>
              <Col xxl={12}>
                <Input type="textarea" rows={3} value={form.notes} onChange={set("notes")} />
              </Col>

              <Col xxl={12} className="mt-4 d-flex gap-2 justify-content-end">
                <button type="button" onClick={() => setMode("view")}
                  style={{ background: "#f3f4f6", color: "#374151", border: "none", borderRadius: "12px", padding: "8px 20px", fontWeight: 600, cursor: "pointer" }}>
                  Cancel
                </button>
                <button type="button" disabled={saving} onClick={handleSave}
                  style={{ background: "#D3178A", color: "#fff", border: "none", borderRadius: "12px", padding: "8px 20px", fontWeight: 600, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1 }}>
                  {saving ? "Saving…" : "Save Changes"}
                </button>
              </Col>
            </>
          )}
        </Row>
      </Container>
    </CommonModal>
  );
};

export default BusinessApplicationModal;